import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { User } from 'app/models/User';
import { LayoutService } from 'app/services/layout.service';
import { PageTitleService } from 'app/services/page-title.service';
import { UserService } from 'app/services/user.service';
import { environment } from 'environments/environment';
import * as signalR from '@microsoft/signalr';
import { AnnouncementService } from 'app/services/Announcement/announcement.service';
import { CreateAnnouncementView } from 'app/models/announcements/CreateAnnouncementView';
import { EndSessionRequest } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-navbar-v2',
	standalone: false,
	templateUrl: './navbar-v2.component.html',
	styleUrl: './navbar-v2.component.css',
})
export class NavbarV2Component implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	environmentName = environment.name;
	isProduction = environment.production;
	isNotificationsOpen = false;
	notifications: CreateAnnouncementView[] = [];
	unreadAnnouncements = 0;
	isNewLayout = false;
	isDropdownOpen = false;
	user: User;
	isMobile = false;

	constructor(
		private userService: UserService,
		public pageTitleService: PageTitleService,
		private router: Router,
		public layoutService: LayoutService,
		private announcementService: AnnouncementService,
		private authService: MsalService
	) {
		this.user = this.userService.currentUserValue.user;
	}

	ngOnInit(): void {
		this.layoutService.isNewLayout$.pipe(takeUntil(this.destroy$)).subscribe((isNew) => {
			this.isNewLayout = isNew;
		});

		if (this.user?.clientId) {
			const connection = new signalR.HubConnectionBuilder()
				.withUrl(`${environment.apiUrl}announcementhub?userId=${this.user?.id}&clientId=${this.user?.clientId}`)
				.build();

			connection
				.start()
				.then(() => {
					this.getAnnouncement();
				})
				.catch((err) => {
					console.error(err);
				});

			connection.on('announcement', () => {
				this.getAnnouncement();
			});

			connection.on('announcementStatusChanged', () => {
				this.getAnnouncement();
			});
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Close dropdowns when clicking outside
	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const target = event.target as HTMLElement;

		// Check if click is outside notification dropdown
		const clickedInsideNotifications = target.closest('.notification-dropdown-container');
		const clickedBell = target.closest('.fa-bell');

		if (!clickedInsideNotifications && !clickedBell && this.isNotificationsOpen) {
			this.isNotificationsOpen = false;
		}

		// Check if click is outside user dropdown
		const clickedInsideUserDropdown = target.closest('.user-dropdown-container');

		if (!clickedInsideUserDropdown && this.isDropdownOpen) {
			this.isDropdownOpen = false;
		}
	}

	toggleNotifications() {
		this.isDropdownOpen = false;

		if (!this.isMobile) {
			this.isNotificationsOpen = !this.isNotificationsOpen;
		} else {
			this.navigateToAnnouncements();
		}
	}

	navigateToAnnouncements() {
		this.isNotificationsOpen = false;
		this.router.navigate(['announcements/view']);
		this.setPageTitle('Announcements');
	}

	toggleDropdown() {
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	getAnnouncement() {
		this.announcementService.getAnnouncementByUserId(this.user?.clientId, this.user?.id).subscribe((response) => {
			response.forEach((announcement) => {
				announcement.isRead = announcement.announcementFors[0]?.isRead;
			});
			this.notifications = response;
			localStorage.setItem('unreadAnnouncementsV2', response.filter((x) => !x.isRead)?.length);
			this.getUnreadAnnouncementCount();
		});
	}

	getUnreadAnnouncementCount() {
		setTimeout(() => {
			this.unreadAnnouncements = Number(localStorage.getItem('unreadAnnouncementsV2'));
			this.getUnreadAnnouncementCount();
		}, 100);
	}

	logout() {
		this.isDropdownOpen = false;
		var isSSOLogin = this.userService.isSSOLogin();
		var msalAccount = this.userService.getMsalAccount();
		this.userService.logout();
		if (isSSOLogin) {
			const logoutRequest: EndSessionRequest = {
				account: msalAccount,
				postLogoutRedirectUri: environment.baseUrl,
			};
			this.authService.logout(logoutRequest);
		}
		this.router.navigateByUrl('/authentication/login').then();
	}

	setPageTitle(section?: string) {
		this.pageTitleService.setPageTitle(section);
	}
}
